/**
 * LocalStoreContext – עבודה לוקאלית בלבד ממגוון הסניף.
 * נתונים נשמרים ב-IndexedDB (מגבלת אחסון גבוהה, עובד ב-Safari/iOS) לפי storeId.
 * אחרי טעינת המגוון – אם עברה שעה מאז "התחל הזמנה", מעדכן מגוון ברקע (ללא תמונות).
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/auth/AuthContext";
import { loadPrefetched } from "@/lib/prefetchStorage";
import { runLightPrefetch } from "@/lib/prefetchOrder";

const LocalStoreContext = createContext(null);

const HOUR_MS = 60 * 60 * 1000;

/** ברירת מחדל: מגוון ישן מעל דקה — מורידים שוב (סדר מוצרים / עדכונים מהמשרד) */
const DEFAULT_STALE_MS = 90_000;
/** לא יותר מרענון קל אחד ל־40 שניות (מניעת הצפה בגלילה בין תתי־קטגוריות) */
const DEFAULT_MIN_INTERVAL_MS = 40_000;

/** מחזיר "דליית שעה" – אותו מספר לכל הדקות באותה שעה (UTC או לוקאל; כאן לוקאל) */
function getHourBucket(ms) {
  return Math.floor(ms / HOUR_MS);
}

export function LocalStoreProvider({ children }) {
  const { user } = useAuth();
  const storeId = user?.store_id?._id ?? user?.store_id ?? null;

  const [localData, setLocalData] = useState(null);
  const [isPrefetchLoading, setIsPrefetchLoading] = useState(!!storeId);
  const [isSyncing, setIsSyncing] = useState(false);
  const lastRefreshedHourBucketRef = useRef(null);
  const catalogTimestampRef = useRef(0);
  const catalogSyncLockRef = useRef({ inFlight: null, lastAttempt: 0 });

  useEffect(() => {
    catalogTimestampRef.current = localData?.timestamp ?? 0;
  }, [localData?.timestamp]);

  useEffect(() => {
    if (!storeId) {
      setLocalData(null);
      setIsPrefetchLoading(false);
      return;
    }
    setIsPrefetchLoading(true);
    loadPrefetched(storeId)
      .then((data) => {
        setLocalData(data);
      })
      .catch(() => setLocalData(null))
      .finally(() => setIsPrefetchLoading(false));
  }, [storeId]);

  const refreshLocal = useCallback(() => {
    if (!storeId) return Promise.resolve();
    return loadPrefetched(storeId).then((data) => {
      setLocalData(data);
      return data;
    });
  }, [storeId]);

  /**
   * מוריד מגוון מעודכן מהשרת אם המטמון המקומי ישן (למשל אחרי שסופר־אדמין שינה סדר מוצרים).
   * מנהלי סניפים עובדים מ־IndexedDB — בלי קריאה כזו הם ימשיכו לראות סדר ישן עד שעה.
   *
   * @param {{ maxAgeMs?: number, minIntervalMs?: number }} [options]
   */
  const ensureFreshCatalog = useCallback(
    async (options = {}) => {
      const maxAgeMs = options.maxAgeMs ?? DEFAULT_STALE_MS;
      const minIntervalMs = options.minIntervalMs ?? DEFAULT_MIN_INTERVAL_MS;
      if (!storeId) return;

      const ts = catalogTimestampRef.current;
      if (Date.now() - ts < maxAgeMs) return;

      const lock = catalogSyncLockRef.current;
      if (Date.now() - lock.lastAttempt < minIntervalMs) return;
      if (lock.inFlight) return lock.inFlight;

      lock.lastAttempt = Date.now();
      const run = async () => {
        setIsSyncing(true);
        try {
          await runLightPrefetch(storeId);
          await refreshLocal();
        } catch (_) {
          lock.lastAttempt = 0;
        } finally {
          setIsSyncing(false);
        }
      };
      const p = run();
      lock.inFlight = p.finally(() => {
        lock.inFlight = null;
      });
      return lock.inFlight;
    },
    [storeId, refreshLocal]
  );

  // כשחוזרים ללשונית – אם המגוון ישן מעל ~3 דקות, נסנכרן (מנהל סניף שחזר מהרקע)
  useEffect(() => {
    if (!storeId) return;
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      const ts = catalogTimestampRef.current;
      if (Date.now() - ts < 3 * 60_000) return;
      ensureFreshCatalog({ maxAgeMs: 3 * 60_000, minIntervalMs: 90_000 });
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [storeId, ensureFreshCatalog]);

  // כשהנתונים נטענו – אם עברה שעה מאז השמירה, עדכון מגוון ברקע (ללא תמונות)
  useEffect(() => {
    if (!storeId || !localData?.timestamp) return;

    const savedBucket = getHourBucket(localData.timestamp);
    const currentBucket = getHourBucket(Date.now());
    if (currentBucket <= savedBucket) return;
    if (lastRefreshedHourBucketRef.current === currentBucket) return;

    lastRefreshedHourBucketRef.current = currentBucket;
    setIsSyncing(true);
    runLightPrefetch(storeId)
      .then(() => refreshLocal())
      .catch(() => {
        lastRefreshedHourBucketRef.current = null;
      })
      .finally(() => setIsSyncing(false));
  }, [storeId, localData?.timestamp, refreshLocal]);

  const value = {
    storeId,
    localData,
    refreshLocal,
    ensureFreshCatalog,
    isPrefetchLoading,
    lastSyncAt: localData?.timestamp ?? null,
    isSyncing,
    hasLocalData: !!(localData?.products?.length),
  };

  return (
    <LocalStoreContext.Provider value={value}>
      {children}
    </LocalStoreContext.Provider>
  );
}

export function useLocalStore() {
  const ctx = useContext(LocalStoreContext);
  if (!ctx) {
    throw new Error("useLocalStore must be used within LocalStoreProvider");
  }
  return ctx;
}
